import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  PageSidebar,
  ProjectOption,
  SkeletonPage,
} from '../../design-system';
import ProjectModalComponent from '../../components/modals/projectModal';
import ImportProjectModal from '../../components/modals/ImportProjectModal';
import { getProjects, listWells } from '../../api/data';
import { Project } from '../../@types/projects/types';
import { useSelected } from '../../providers/selectedContext';
import { useAppContext } from '../../providers/AppContext';
import { useAuth } from '../../providers/logincontext';
import NotificationCenterButton from '../../components/nav/NotificationCenterButton';
import styles from '../../pages/CapillaryPressurePage/CapillaryPressurePage.module.css';

interface ProjectShellProps {
  children: React.ReactNode;
}

const ProjectShell = ({ children }: ProjectShellProps) => {
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState<Project | null>(null);
  const [allProjects, setAllProjects] = useState<Project[]>([]);

  const { data, updateData, updateConfigStates } = useAppContext();
  const { setSelectedInfo } = useSelected();
  const { currentUser, username } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const projectUid = new URLSearchParams(location.search).get('project');

  useEffect(() => {
    getProjects()
      .then(projects => {
        setAllProjects(projects);
        updateData({ projects });
        const found = projects.find(p => p.uid === projectUid) ?? null;
        setProject(found);
        // Clear any sample/well carried over from a previously selected
        // project so switching projects client-side doesn't show stale data.
        if (found) setSelectedInfo({ project: found, sample: undefined, well: undefined });
      })
      .catch(err => console.error('Error loading project:', err))
      .finally(() => setLoading(false));
  }, [projectUid, setSelectedInfo, updateData]);

  useEffect(() => {
    if (!Array.isArray(data.projects) || data.projects.length === 0) return;

    setAllProjects(data.projects);
    const found = data.projects.find(p => p.uid === projectUid) ?? null;
    setProject(found);
  }, [data.projects, projectUid]);

  useEffect(() => {
    if (!projectUid) return;
    listWells(projectUid)
      .then(wells => updateData({ wells: Array.isArray(wells) ? wells : [] }))
      .catch(() => {});
  }, [projectUid, updateData]);

  const switchProject = useCallback((uid: string) => {
    if (uid === projectUid) return;
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('project', uid);
    // Client-side navigation keeps the live auth context. A hard
    // `window.location.href` reload wiped the in-memory access token, so during
    // keycloak's async re-init ProtectedRoute bounced to /login and then
    // PublicOnlyRoute bounced to /projects, dropping the ?project= param.
    navigate(`${location.pathname}?${searchParams.toString()}`);
  }, [navigate, projectUid, location.pathname, location.search]);

  const projectOptions: ProjectOption[] = allProjects.map(p => ({
    id: p.uid,
    name: p.project_name ?? p.uid,
    code: (p.project_name ?? p.uid).slice(0, 3).toUpperCase(),
    active: p.uid === projectUid,
    onClick: () => switchProject(p.uid)
  }));

  const goBack = useCallback(() => {
    navigate('/projects');
  }, []);

  if (loading) {
    return <SkeletonPage />;
  }

  return (
    <div className={styles.shell}>
      <PageSidebar
        data-tour="sidebar"
        className={styles.sidebar}
        projectName={project?.project_name ?? undefined}
        onBackToProjects={goBack}
        projects={projectOptions}
        onNewProject={() => {
          updateConfigStates({ showProject: true });
        }}
        onImportProject={() => {
          updateConfigStates({ showImportProject: true });
        }}
        onProjectSettings={() => {
          navigate('/projects');
        }}
        user={
          currentUser
            ? {
              name: username ?? currentUser.email.split('@')[0],
            }
            : undefined
        }
        userAction={
          <NotificationCenterButton
            placement="rightBottom"
            triggerClassName={styles.sidebarBell}
            iconStyle={{ color: 'var(--ds-emerald-zinc-100)' }}
          />
        }
      />

      <main className={styles.main}>
        {children}
      </main>

      <ProjectModalComponent />
      <ImportProjectModal />
    </div>
  );
};

export default ProjectShell;
